import React, { useState, useCallback } from 'react'
import DashboardLayout from '../../../../../layouts/Dashboard'
import { Col, Row, Form, Container, Button, Spinner } from 'react-bootstrap'
import api from '../../../../../services/api'
import { toast } from 'react-toastify'
import { errorApi } from '../../../../../utils/errorApi'
import { useNavigate } from 'react-router-dom'
import { IMaskInput } from 'react-imask'
import SearchInput from '../../../../../components/SearchInput'

import styles from './styles.module.css'
import { useLoading } from '../../../../../hooks/LoadingContext'
import LoadingSpinner from '../../../../../components/LoadingSpinner/LoadingSpinner'

interface IVehicle {
  id: number
  name: string
}

interface IDriver {
  id: number
  name: string
}

const AbastecimentosNovo: React.FC = () => {
  const [gasStation, setGasStation] = useState<string>('')
  const [gasType, setGasType] = useState<string>('')
  const [cost, setCost] = useState<number>()
  const [costString, setCostString] = useState<string>('')
  const [quantity, setQuantity] = useState<number>()
  const [quantityString, setQuantityString] = useState<string>('')
  const [mileage, setMileage] = useState<number>()
  const [mileageString, setMileageString] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [imageNote, setImageNote] = useState<File | null>(null)
  const [imageFuel, setImageFuel] = useState<File | null>(null)

  const [vehicleId, setVehicleId] = useState<number>()
  const [driverId, setDriverId] = useState<number>()
  const [vehicles, setVehicles] = useState<IVehicle[]>()
  const [drivers, setDrivers] = useState<IDriver[]>()
  const [showVehicles, setShowVehicles] = useState<boolean>(false)
  const [showDrivers, setShowDrivers] = useState<boolean>(false)

  const { isLoading, setIsLoading } = useLoading()
  const navigate = useNavigate()

  const isValidModel = () => {
    if (!gasStation || !quantity || !cost || !mileage || !date || !imageNote || !vehicleId)
      return false
    else return true
  }

  const gasTypeNamesMap = {
    normal_gasoline: 'gasolina comum',
    added_gasoline: 'gasolina aditivada',
    premium_gasoline: 'gasolina premium',
    formulated_gasoline: 'gasolina formulada',
    ethanol: 'etanol',
    added_ethanol: 'etanol aditivado',
    gnv: 'GNV',
    s10_diesel: 'diesel S10',
    added_diesel: 'diesel aditivado',
    premium_diesel: 'diesel premium',
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    setIsLoading(true)

    if (!isValidModel()) {
      toast.error('Você precisa preencher todos os campos obrigatórios!')
      setIsLoading(false)
      return
    }
    const data = new FormData()

    const model = {
      vehicle_id: vehicleId,
      driver_id: driverId,
      gas_station: gasStation,
      fuel_type: gasType,
      amount: quantity,
      cost: cost,
      mileage: mileage,
      date: new Date(date).toISOString(),
    }

    data.append('data', JSON.stringify(model))
    if (imageNote) data.append('invoice', imageNote, imageNote.name)
    if (imageFuel) data.append('previous_fuel_level', imageFuel, imageFuel.name)

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }

    try {
      await api.post(`delivery-manager/vehicle/refuellings`, data, config)
      toast.info('Abastecimento cadastrado com sucesso!', { autoClose: 3000 })
      setIsLoading(false)
      navigate('/gestor-entregas/veiculos/abastecimentos')
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao cadastrar o abastecimento! Verifique os dados.'))
      setIsLoading(false)
    }
  }

  const clearFileInput = (e: any) => {
    const filesData = new DataTransfer()
    e.target.files = filesData.files
  }

  const handleInvoiceChange = (e: any) => {
    if (e.target.files.length !== 0) {
      const file = e.target.files[0]
      const fileType = file.name.split('.')[1].toLowerCase()

      if (!['jpg', 'png', 'gif', 'jpeg'].includes(fileType)) {
        toast.error('A foto deve possuir formato .PNG, .JPG, .JPEG ou .GIF')
        clearFileInput(e)
        setImageNote(null)
      } else setImageNote(e.target.files[0])
    }
  }

  const handleFuelLevelChange = (e: any) => {
    if (e.target.files.length !== 0) {
      const file = e.target.files[0]
      const fileType = file.name.split('.')[1].toLowerCase()

      if (!['jpg', 'png', 'gif', 'jpeg'].includes(fileType)) {
        toast.error('A foto deve possuir formato .PNG, .JPG, .JPEG ou .GIF')
        clearFileInput(e)
        setImageFuel(null)
      } else setImageFuel(e.target.files[0])
    }
  }

  const loadDriverData = useCallback(async (searchDriver: string) => {
    setIsLoading(true)
    let apiUrl = '/delivery-manager/drivers?limit=10&show_invalid=1'

    if (searchDriver) apiUrl += '&name=' + searchDriver

    try {
      const { data } = await api.get(apiUrl)

      if (!data.drivers.length) {
        toast.warning('Não foram encontrados resultados para a pesquisa!')
        setDrivers(undefined)
      } else {
        setDrivers(
          data.drivers.map((driver: { user_id: any; name: any }) => {
            return { id: driver.user_id, name: driver.name }
          }),
        )
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleVehicleSearch = (searchBoard: string) => {
    loadVehicleData(searchBoard)
    setShowVehicles(true)
  }

  const loadVehicleData = useCallback(async (searchBoard: string) => {
    setIsLoading(true)
    let apiUrl = '/delivery-manager/vehicles?limit=10&show_invalid=1'

    if (searchBoard) apiUrl += '&board=' + searchBoard

    try {
      const { data } = await api.get(apiUrl)

      if (!data.length) {
        toast.warning('Não foram encontrados resultados para a pesquisa!')
        setVehicles(undefined)
      } else {
        setVehicles(
          data.map((vehicle: { id: any; board: any; model: any }) => {
            return {
              id: vehicle.id,
              name: `${vehicle.board} - ${vehicle.model}`,
            }
          }),
        )
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDriverSearch = (searchDriver: string) => {
    loadDriverData(searchDriver)
    setShowDrivers(true)
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Cadastrar abastecimento">
      <LoadingSpinner text="Salvando informações. Aguarde..." />
      <Container>
        <h2 style={{ textAlign: 'center' }}>Novo Abastecimento</h2>
        <Col className={styles.formContainer}>
          <Row>
            <Col md={6}>
              <Row style={{ marginTop: 15 }}>
                <Form.Label className={styles.label}>Nome do motorista</Form.Label>
                <SearchInput
                  data={drivers}
                  showOptions={showDrivers}
                  setValue={setDriverId}
                  handleClear={() => setShowDrivers(false)}
                  handleSearch={handleDriverSearch}
                  placeholder="Digite o nome do motorista"
                  edit={false}
                />
              </Row>
            </Col>

            <Col md={6}>
              <Row style={{ marginTop: 15 }}>
                <Form.Label className={styles.label}>Placa do veículo*</Form.Label>
                <SearchInput
                  data={vehicles}
                  showOptions={showVehicles}
                  setValue={setVehicleId}
                  handleClear={() => setShowVehicles(false)}
                  handleSearch={handleVehicleSearch}
                  placeholder="Digite a placa do veículo"
                  edit={false}
                />
              </Row>
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Posto de combustível*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui o posto de combustível"
                value={gasStation}
                onChange={(event) => setGasStation(event.target.value)}
              />
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Tipo de combustível*</Form.Label>
              <Form.Select
                className={styles.input}
                value={gasType}
                onChange={(event) => setGasType(event.target.value)}
              >
                <option value="">Selecione um tipo de combustível</option>
                {Object.entries(gasTypeNamesMap).map(([key, gas]) => {
                  return (
                    <option key={key} value={key}>
                      {gas}
                    </option>
                  )
                })}
              </Form.Select>
            </Col>
          </Row>

          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Quantidade (litros)*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui a quantidade de combustível em litros"
                as={IMaskInput}
                mask={[
                  {
                    mask: Number,
                    radix: ',',
                  },
                ]}
                value={quantityString}
                onChange={(event) => {
                  setQuantity(Number(event.target.value.replace(',', '.')) || 0)
                  setQuantityString(event.target.value)
                }}
              />
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Custo*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui o custo do abastecimento"
                as={IMaskInput}
                mask={[
                  {
                    mask: Number,
                    radix: ',',
                  },
                ]}
                value={costString}
                onChange={(event) => {
                  setCost(Number(event.target.value.replace(',', '.')) || 0)
                  setCostString(event.target.value)
                }}
              />
            </Col>
          </Row>

          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Quilometragem atual*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui a quilometragem do veículo"
                as={IMaskInput}
                mask={[
                  {
                    mask: Number,
                    radix: ',',
                  },
                ]}
                value={mileageString}
                onChange={(event) => {
                  setMileage(Number(event.target.value.replace(',', '.')) || 0)
                  setMileageString(event.target.value)
                }}
              />
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Data*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui a data do abastecimento"
                type="datetime-local"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Nota fiscal*</Form.Label>
              <Form.Control
                className={styles.input}
                type="file"
                onChange={(event) => handleInvoiceChange(event)}
              />
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>
                Nível de combustível antes do abastecimento
              </Form.Label>
              <Form.Control
                className={styles.input}
                type="file"
                onChange={(event) => handleFuelLevelChange(event)}
              />
            </Col>
          </Row>

          <Col style={{ marginTop: 15, textAlign: 'right' }}>
            <Button
              className={styles.btn_submit}
              disabled={isLoading || !isValidModel()}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{
                    color: '#fff',
                    marginRight: 10,
                  }}
                />
              ) : (
                'Cadastrar abastecimento'
              )}
            </Button>
          </Col>
        </Col>
      </Container>
    </DashboardLayout>
  )
}

export default AbastecimentosNovo
